import { ArrowBack, ChevronRight } from '@mui/icons-material';
import { Avatar, Box, Button, CircularProgress, Chip, Typography, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import theme from '../../../../../theme';
import { setAlertDialog } from '../../../../../toolkit/alertDialog/alertDialogSlice';
import { localAuction } from '../../../../../utils/constants/auction';
import { getRGBAFromColor } from '../../../../../utils/methods/getCssVariables';
import { getCommaFormattedNumber } from '../../../../../utils/methods/stringMethods';

const NegotiationList = ({
  selectedCar,
  negotiationList = [],
  onBidderClick,
  onPrevSectionHandler,
  isLoading = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('negotiationList');

  console.log(negotiationList, `negotiationList`);

  return (
    <>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          gap: '1rem',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <IconButton
          sx={{ p: 0, m: 0 }}
          onClick={() => {
            if (onPrevSectionHandler) {
              onPrevSectionHandler();
            }
          }}
        >
          <ArrowBack
            sx={{
              fontSize: '1.25rem',
              padding: '0.25rem',
              color: theme.palette.primary.main,
              backgroundColor: getRGBAFromColor({
                color: theme.palette.primary.main,
                alpha: '0.1',
              }),
              borderRadius: '8px',
            }}
          />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            align="left"
            fontWeight="600"
            color={theme.palette.primary.main}
            sx={{
              fontSize: '1.125rem',
              marginBottom: '0.25rem',
            }}
          >
            {t('Negotiation List')}
          </Typography>
        </Box>
      </Box>

      {/* Negotiation List Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <Typography
          variant="body2"
          align="left"
          sx={{
            fontSize: '0.875rem',
          }}
        >
          {t('Negotiate with the dealer and finalize your deal.')}
        </Typography>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '3rem 0',
            }}
          >
            <CircularProgress size={32} thickness={4} />
          </Box>
        ) : negotiationList?.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              textAlign: 'center',
              border: `1px solid ${getRGBAFromColor({
                color: theme.palette.text.primary,
                alpha: '0.1',
              })}`,
              borderRadius: '0.75rem',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                opacity: 0.7,
                fontWeight: 500,
              }}
            >
              {t('No negotiations available')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                marginTop: '0.5rem',
              }}
            >
              {t('Check back later for new offers')}
            </Typography>
          </Box>
        ) : (
          negotiationList?.map((currBidder, index) => {
            const isInactive = currBidder?.bidder_status === 'inactive';

            return (
              <Box
                key={currBidder?.dealerId || index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: isInactive
                    ? getRGBAFromColor({
                        color: theme.palette.text.primary,
                        alpha: '0.05',
                      })
                    : theme.palette.background.paper,
                  border: `1px solid ${getRGBAFromColor({
                    color: theme.palette.text.primary,
                    alpha: isInactive ? '0.1' : '0.12',
                  })}`,
                  padding: '1rem',
                  borderRadius: '0.75rem',
                  cursor: isInactive ? 'not-allowed' : 'pointer',
                  opacity: isInactive ? 0.6 : 1,
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: isInactive
                    ? 'none'
                    : `0 1px 3px ${getRGBAFromColor({
                        color: theme.palette.text.primary,
                        alpha: '0.08',
                      })}`,
                  '&:hover': {
                    ...(!isInactive && {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${getRGBAFromColor({
                        color: theme.palette.primary.main,
                        alpha: '0.15',
                      })}`,
                      borderColor: getRGBAFromColor({
                        color: theme.palette.primary.main,
                        alpha: '0.3',
                      }),
                    }),
                  },
                }}
                onClick={() => {
                  if (isInactive) {
                    dispatch(
                      setAlertDialog({
                        open: true,
                        title: t('Alert!'),
                        message: t('This dealer is currently inactive!'),
                      })
                    );
                    return;
                  }

                  if (onBidderClick) {
                    onBidderClick(currBidder);
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flex: 1,
                  }}
                >
                  <Avatar
                    src="https://randomuser.me/api/portraits/men/32.jpg"
                    alt="Dealer"
                    sx={{
                      width: 52,
                      height: 52,
                      border: `2px solid ${getRGBAFromColor({
                        color: theme.palette.primary.main,
                        alpha: '0.2',
                      })}`,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.25rem',
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: '0.95rem',
                        }}
                      >
                        {selectedCar?.auction_type === localAuction ? t('Dealer ID') : t('Buyer ID')}:{' '}
                        {currBidder?.dealerId}
                      </Typography>
                      {isInactive && (
                        <Chip
                          label={t('Inactive')}
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            backgroundColor: getRGBAFromColor({
                              color: theme.palette.error.main,
                              alpha: '0.15',
                            }),
                            color: theme.palette.error.main,
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.success.main,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      {t('Best Offer:')} ${getCommaFormattedNumber(currBidder?.amount)}
                    </Typography>
                  </Box>
                </Box>

                {!isInactive && (
                  <ChevronRight
                    sx={{
                      fontSize: '1.5rem',
                      color: theme.palette.text.secondary,
                      opacity: 0.5,
                    }}
                  />
                )}
              </Box>
            );
          })
        )}
      </Box>
    </>
  );
};

export default NegotiationList;
